<template>
	<v-container id="data-entry" class="pa-0" height="100%" fluid>
		<v-overlay
			:model-value="loading && !loadFailed"
			class="align-center justify-center sde-loading-overlay"
		>
			<v-progress-circular color="primary" indeterminate size="64" />
		</v-overlay>

		<v-overlay
			:model-value="loadFailed"
			class="align-center justify-center"
			persistent
			no-click-animation
		>
			<v-alert
				icon="mdi-close-circle"
				class="bg-valence-negative-tint text-valence-negative-ink"
				max-width="560"
			>
				<div class="text-body-1 mb-4">
					This extraction failed to load. Nothing can be viewed or saved until
					it loads successfully.
				</div>
				<div class="d-flex justify-end ga-2">
					<v-btn
						class="bg-primary"
						elevation="2"
						size="small"
						@click="retryLoad"
					>
						Retry
					</v-btn>
					<v-btn elevation="2" size="small" @click="returnHomeAfterLoadFailure">
						Return to dashboard
					</v-btn>
				</div>
			</v-alert>
		</v-overlay>

		<!--
			DCTL-2459: the validation dialog can now be dismissed without losing
			the page, so the "your last save was refused" state needs a signal
			that outlives the dialog. This banner stays until a save succeeds.
		-->
		<v-alert
			v-if="saveRejectedByValidation"
			icon="mdi-alert-circle"
			class="save-rejected-banner text-body-2 bg-valence-negative-tint text-valence-negative-ink"
			density="compact"
		>
			<div class="d-flex align-center ga-3">
				<span>
					Your last save was refused and this extraction is not saved. Fix the
					flagged record, then save again.
				</span>
				<v-btn
					class="flex-shrink-0 bg-grey"
					elevation="2"
					size="small"
					@click="validationDialog = true"
				>
					Show details
				</v-btn>
			</div>
		</v-alert>

		<iframe :id="module" ref="iframe" src="/modules/wpd/app/index.html" />

		<v-metadata-v2
			v-if="showMetadata"
			ref="metadataV2"
			:bounds="bounds"
			:is-admin="isAdmin"
			:read-only="readOnly"
			:tags="tags"
			:tag-groups="tagGroups"
			:has-qc-comments-panel="qcCommentsOpen"
		/>

		<v-metadata-preview
			v-if="showMetadataPreview && !viewEditOverlappingDataPoints"
			ref="metadataPreview"
			:bounds="bounds"
		/>

		<v-metadata-preview-v2
			v-if="showMetadataPreview && viewEditOverlappingDataPoints"
			ref="metadataPreview"
			:bounds="bounds"
		/>

		<v-metadata-select-dialog
			v-if="viewEditOverlappingDataPoints"
			ref="metadataSelectDialog"
			:bounds="bounds"
		/>

		<switch-tag-group-dialog ref="switchTagGroupDialog" />

		<v-hint ref="hint" :bounds="bounds" />

		<data-entry-menu
			v-if="!qcMode"
			@open-file-in-new-tab="openFileInNewTab"
			@open-raw-data-viewer="openRawDataViewer"
			@view-paper-citation="paperCitationDialog = true"
			@toggle-comments="qcCommentsOpen = !qcCommentsOpen"
		/>

		<data-entry-paper-citation-dialog
			v-model="paperCitationDialog"
			:paper="paper"
		/>

		<data-entry-validation-dialog
			v-model="validationDialog"
			:validation-data="validationData"
			:module-window="moduleWindow"
			:sde-uuid="sdeUuid"
			:done="done"
			:triggered-by-save="validationTriggeredBySave"
		/>

		<data-entry-pre-rework-dialog v-model="preReworkDialog" />

		<v-raw-data-viewer
			v-if="showRawDataViewer"
			v-model="showRawDataViewer"
			:raw-data="rawData"
			:file-names="fileNames"
			:module-window="moduleWindow"
			@update-json="updateJson"
		/>

		<qc-comments-panel
			v-model="qcCommentsOpen"
			:sde-uuid="sdeUuid"
			:active-data-point-uuid="dataPointUuid"
			:highlight-comment-uuid="$route?.query?.commentUuid || null"
			@navigate="handleQcCommentNavigate"
		/>

		<v-dialog v-model="unavailableDialogOpen" max-width="500" persistent>
			<v-card>
				<v-toolbar color="primary" dark flat>
					<v-toolbar-title>Paper no longer available</v-toolbar-title>
				</v-toolbar>
				<v-card-text class="pt-4">
					<p class="text-body-1">
						This paper has been claimed by other extractors and is no longer
						available. Your in-progress work could not be saved. Please return
						to the queue to pick up another paper.
					</p>
				</v-card-text>
				<v-card-actions class="px-4 pb-4">
					<v-spacer />
					<v-btn
						class="bg-secondary"
						elevation="2"
						@click="returnToQueueAfterUnavailable"
					>
						Return to queue
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog
			:model-value="savingStopped"
			max-width="600"
			persistent
			no-click-animation
		>
			<v-card class="pb-5">
				<v-toolbar color="primary" dark flat>
					<v-toolbar-title>Saving has stopped</v-toolbar-title>
				</v-toolbar>
				<v-card-text class="pa-10 text-center text-subtitle-1">
					Your session ended or this page lost its connection to the extraction.
					<br />
					<br />
					<span v-if="lastSaveTimeLabel">
						Changes made after {{ lastSaveTimeLabel }} have not been saved.
					</span>
					<span v-else>Your recent changes have not been saved.</span>
					Reload to continue.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn
						class="mr-3 bg-secondary"
						elevation="2"
						autofocus
						@click="reloadAfterSavingStopped"
					>
						Reload page
					</v-btn>
					<v-btn elevation="2" @click="returnHomeAfterSavingStopped">
						Return to home
					</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import _ from "lodash";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import interfaces from "@/plugins/interfaces";
import utils from "@/utils";
import tabManager from "@/utils/tabManager";
import { createPdfViewerHtml } from "@/utils/pdfViewer";
import { formatSaveTime, shouldBlockSaving } from "@/utils/savingStopped";
import { useLDFlag } from "launchdarkly-vue-client-sdk";
import * as pdfjsV4 from "pdfjs-dist";

// WPD's bundled pdfjs 2.9 merges tab-separated text runs into a single span,
// which breaks direct-select-text on PDFs that use tabs for justified spacing
// (e.g. Harland 2020). We render the text layer using outer pdfjs-dist v4
// instead, which emits word-level items. See pdf-manager.js for the iframe
// side of the hand-off.
pdfjsV4.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

import Timer from "@/assets/scripts/autosave-timer.worker.js?worker";

import VHint from "@/components/VHint";
import VMetadataV2 from "@/components/VMetadata/VMetadataV2.vue";
import VMetadataPreview from "@/components/VMetadataPreview";
import VMetadataPreviewV2 from "@/components/VMetadataPreview/VMetadataPreviewV2.vue";
import VMetadataSelectDialog from "@/components/VMetadataSelectDialog";
import SwitchTagGroupDialog from "@/components/SwitchTagGroupDialog.vue";
import VRawDataViewer from "@/pages/RawDataViewer/VRawDataViewer.vue";
import DataEntryMenu from "@/pages/DataEntry/DataEntryMenu";
import DataEntryPaperCitationDialog from "@/pages/DataEntry/DataEntryPaperCitationDialog";
import DataEntryPreReworkDialog from "@/pages/DataEntry/DataEntryPreReworkDialog";
import DataEntryValidationDialog from "@/pages/DataEntry/DataEntryValidationDialog";
import QcCommentsPanel from "@/components/QcCommentsPanel.vue";
import tableAxisInstruction from "@/assets/images/table_axis_instruction.gif";
import createCompositeKeyUtils from "@/compositeKeyUtils";
import { ref, onMounted, markRaw } from "vue";
import { v4 as uuidv4 } from "uuid";
import * as jsonpatch from "fast-json-patch";
import indefinite from "indefinite";

const autoSaveWait = 300000; // 5 mins
const SAVE_CONNECTION_LOST_MESSAGE =
	"Connection lost. Your latest changes are not saved yet. Keep this tab open; saving will resume automatically.";

export default {
	name: "DataEntry",

	components: {
		DataEntryMenu,
		DataEntryPaperCitationDialog,
		DataEntryPreReworkDialog,
		DataEntryValidationDialog,
		VHint,
		VMetadataV2,
		VMetadataPreview,
		VMetadataPreviewV2,
		VMetadataSelectDialog,
		VRawDataViewer,
		QcCommentsPanel,
		SwitchTagGroupDialog,
	},

	async beforeRouteLeave(to, from, next) {
		if (this.qcMode) return next();
		if (
			!this.readOnly &&
			this.isMounted &&
			!this.skipDirtyCheck &&
			this.checkDirty()
		) {
			this.$emit("deactivate-activity-timer");

			// NOTE - The vue router beforeRouteLeave is only hit after the first attempt to
			// navigate away; a subsequent click will bypass this function.  To prevent data loss,
			// save after cancelling the first navigation.
			// NOTE - session timeouts can cause the code to reach here without
			// authentication
			if (this.isAuthenticated) {
				if (
					!confirm("You have unsaved changes. Do you really want to leave?")
				) {
					await this.timedAutoSave();
					this.$emit("activate-activity-timer");
					return next(false);
				}

				this.$emit("activate-activity-timer");
			}
		}

		// teardown lives in beforeUnmount: a later guard can still cancel this
		// navigation and the page must stay fully alive if it does (DCTL-2654)
		next();
	},

	props: {
		paperUuid: {
			type: String,
			required: true,
		},
		readOnly: Boolean,
		sdeUuid: {
			type: String,
			required: true,
		},
		dataPointUuid: {
			type: String,
			required: false,
			default: null,
		},
		training: Boolean,
		showBanner: {
			type: Boolean,
			default: false,
		},
		isAdmin: {
			type: Boolean,
			default: false,
		},
		qcMode: {
			type: Boolean,
			default: false,
		},
	},

	emits: [
		"activate-activity-timer",
		"deactivate-activity-timer",
		"check-dirty",
		"handle-logout-save",
		"load-state",
	],

	setup() {
		const showMetadata = ref(false);
		onMounted(() => {
			setTimeout(() => {
				showMetadata.value = true;
			}, 100);
		});

		const showMetadataPreview = ref(false);
		onMounted(() => {
			setTimeout(() => {
				showMetadataPreview.value = true;
			}, 100);
		});

		const updateDataPointsOnBarHeaderEdit = useLDFlag(
			"update-data-points-on-bar-header-edit",
		);

		const pointGroupColors = useLDFlag("graph-point-group-coloring");
		const pointGroupOverhaul = useLDFlag("point-group-overhaul");
		const undoRedoEnabled = useLDFlag("sde-undo-redo");
		const reworkXYGraphCalibration = useLDFlag("rework-xy-graph-calibration");
		const viewEditOverlappingDataPoints = useLDFlag(
			"view-edit-overlapping-data-points",
		);
		const textSelectCharacterSanitization = useLDFlag(
			"text-select-character-sanitization",
		);
		const pointGroupProximityFixes = useLDFlag("point-group-proximity-fixes");
		const moveTextDataBetweenTagGroups = useLDFlag(
			"move-text-data-between-tag-groups",
		);

		return {
			showMetadata,
			showMetadataPreview,
			updateDataPointsOnBarHeaderEdit,
			pointGroupColors,
			pointGroupOverhaul,
			undoRedoEnabled,
			reworkXYGraphCalibration,
			viewEditOverlappingDataPoints,
			textSelectCharacterSanitization,
			pointGroupProximityFixes,
			moveTextDataBetweenTagGroups,
		};
	},

	data() {
		return {
			autoSaveComplete: true,
			// SHA-256 hex of the exact raw JSON string this client loaded as its
			// diff baseline (the string in saveData.data before JSON.parse), or
			// null when the baseline is legitimately empty (new extraction /
			// pre-rework restart). Sent with every WPD save so the server can
			// reject a save whose baseline is not the currently stored doc
			// (409 baseline_conflict).
			baselineHash: null,
			// Web Crypto is unavailable in non-secure contexts; in that case the
			// baseline fields are omitted entirely (server treats the request as
			// a legacy client) rather than sending a false "empty baseline".
			baselineSupported: !!window.crypto?.subtle,
			bounds: null,
			compositeKeyUtils: null,
			existingData: markRaw({}),
			fileMap: {},
			lastWpdHash: null,
			lastSnapshotJson: null,
			undoStack: [],
			redoStack: [],
			maxHistory: 20,
			isProgrammaticChange: false,
			isMounted: false,
			isUnmounted: false,
			// True only once mounted() has fully succeeded (paper fetched,
			// iframe initialized, JSON loaded). Every save/dirty/validate/
			// reset/discard path is gated on it: before that point the WPD
			// singleton holds no document of this page's (or a stale one from
			// a previous extraction), and saving from that state is what
			// caused the Jul 2026 cross-SDE corruption. Cleared again on
			// unmount so stale App.vue callbacks (check-dirty/logout-save)
			// report not-dirty instead of serializing a disposed interface.
			loadComplete: false,
			// Terminal error state: getPaper failed. mounted() stops before
			// iframeInit/activateAutoSave and the template shows a retry/back
			// affordance instead of proceeding with an empty document.
			loadFailed: false,
			loading: true,
			paperCitationDialog: false,
			paperData: null,
			menu: false,
			moduleWindow: null,
			preReworkDialog: false,
			rawData: {},
			showRawDataViewer: false,
			qcCommentsOpen: false,
			skipDirtyCheck: false,
			tags: [],
			tagGroups: [],
			timer: null,
			validationData: null,
			validationDialog: false,
			validationTriggeredBySave: false,
			// DCTL-2459: set when the server REFUSES a save under
			// `prevent-invalid-wpd-saves`. Nothing is stored in that case, so the
			// curator has to know their work is still only in this page. Cleared
			// by the next save that succeeds.
			saveRejectedByValidation: false,
			// Set when a save POST got no response (offline, dropped socket).
			// The warning it raises stays until a save gets through.
			saveConnectionLost: false,
			// Set when the save-time availability guard rejects (the paper
			// has been claimed past the 2-extractor cap by other users
			// between "Start Extraction" and this first save). Shows a
			// dialog explaining the situation and routes the user back
			// to the queue on dismiss.
			unavailableDialogOpen: false,
			// DCTL-2654: set once the session or the store paper is gone while
			// this page still renders a loaded document. Every save path stops
			// and the blocking dialog is the only surface from then on.
			savingStopped: false,
			// when the stored document last matched this page (load or save)
			lastSuccessfulSaveAt: null,
		};
	},

	computed: {
		dirty() {
			return useMainStore().dirty;
		},
		discardSde() {
			return useMainStore().discardSde;
		},
		domain() {
			return import.meta.env.VUE_APP_WEB_HOST;
		},
		done() {
			return useMainStore().done;
		},
		exit() {
			return useMainStore().exit;
		},
		fileNames() {
			return Object.values(this.fileMap).map((file) => file.name);
		},
		isAuthenticated() {
			return useMainStore().isAuthenticated;
		},
		lastSaveTimeLabel() {
			return formatSaveTime(this.lastSuccessfulSaveAt);
		},
		module() {
			return useMainStore().module;
		},
		paper() {
			return useMainStore().paper;
		},
		protocol() {
			return import.meta.env.VUE_APP_ENV === "development" ? "http" : "https";
		},
		resetDataEntry() {
			return useMainStore().resetDataEntry;
		},
		resolve() {
			return useMainStore().resolve;
		},
		save() {
			return useMainStore().save;
		},
		saveInProgress() {
			return useMainStore().saveInProgress;
		},
		saveUUID() {
			return useMainStore().saveUUID;
		},
		validate() {
			return useMainStore().validate;
		},
	},

	watch: {
		discardSde(val) {
			if (val) {
				this.discardSDE();
			}
		},
		isAuthenticated(val) {
			if (!val) {
				this.stopSaving();
			}
		},
		paper(val) {
			// the page clears the paper itself after a "done" save
			if (_.isEmpty(val) && !this.done) {
				this.stopSaving();
			}
		},
		resetDataEntry(val) {
			if (val) {
				this.resetJSON();
			}
		},
		save(val) {
			if (!val) {
				return;
			}
			// Load gate: pre-load there is nothing of this page's to save, and
			// interfaces.saveJSON() would either throw (disposed singleton) or
			// serialize a document that is not this extraction's. Consume the
			// event visibly instead.
			if (!this.loadComplete) {
				this.handleSaveBeforeLoad();
				return;
			}
			this.saveJSON(interfaces[this.module].saveJSON());
		},
		validate(json) {
			if (!json) {
				return;
			}
			// Load gate: same reasoning as the save watcher above.
			if (!this.loadComplete) {
				this.showNotification({
					message:
						"This extraction is still loading. Validation is not available yet.",
					status: "warning",
				});
				this.receiveValidate();
				return;
			}
			this.validateJSON(interfaces[this.module].saveJSON());
		},
		showBanner() {
			this.$refs.iframe.contentWindow.postMessage({ type: "resize" }, "*");
		},
	},

	async mounted() {
		// make sure tags and tag groups are resolved before initializing the
		// iframe
		await this.waitForIframeLoad();
		await this.getPaper();

		// Hard-fail on a failed paper load: never continue into iframeInit /
		// activateAutoSave with empty tags and an empty document. loadComplete
		// stays false, so no save path can run; the template shows a terminal
		// error state with retry/back affordances.
		if (this.loadFailed) {
			this.loading = false;
			return;
		}

		this.compositeKeyUtils = createCompositeKeyUtils(this.tags);

		if (!this.isUnmounted) {
			await this.iframeInit();
			// initialize WPD change detection after WPD has loaded files/data
			if (this.undoRedoEnabled) {
				try {
					const initialJson = interfaces[this.module].saveJSON();
					this.lastWpdHash = this.computeHash(initialJson);
					this.lastWpdJson = initialJson;
					this.lastSnapshotJson = initialJson;
					this.undoStack = [
						{ json: initialJson, path: { root: null, axis: null } },
					];
					this.redoStack = [];
				} catch (_) {
					this.lastWpdHash = null;
				}
				window.addEventListener("message", this.wpdActivityMessageHandler);
			}
			if (!this.qcMode) {
				this.activateAutoSave();
			}

			// The page's own document is now fully loaded; save/dirty paths
			// may run. Only ever set here - never on an error path.
			this.loadComplete = true;
			this.lastSuccessfulSaveAt = new Date();
		}

		this.isMounted = true;

		// listen for messages from the raw data viewer tab and parent frames
		window.addEventListener("message", this.handleMessage);

		// Auto-open comments panel if requested via query param
		if (this.$route?.query?.comments === "true") {
			this.qcCommentsOpen = true;
		}
	},

	beforeUnmount() {
		// destroy the autosave worker
		this.deactivateAutoSave();
		this.timer?.terminate();
		this.timer = null;
		this.isUnmounted = true;
		this.isMounted = false;

		// this page's document is going away: close the load gate so stale
		// App.vue callbacks (check-dirty/logout-save) report not-dirty, and
		// release the WPD singleton so any save fired before the next init()
		// throws loudly instead of serializing this page's stale document
		this.loadComplete = false;
		interfaces[this.module].dispose();

		// leave no page state behind for the next route
		this.clearDirty();
		this.preReworkDialog = false;
		this.validationDialog = false;
		this.loading = false;
		this.clearNotification();

		// remove forwarded-event listeners registered in iframeInit (also
		// covers remounts that bypass beforeRouteLeave, e.g. a param-only
		// navigation remounting via the router-view key)
		document.removeEventListener("keydown", this.keyDownHandler);
		document.removeEventListener("next-cell", this.nextCellHandler);

		// remove message listener
		window.removeEventListener("message", this.handleMessage);

		if (this.undoRedoEnabled) {
			window.removeEventListener("message", this.wpdActivityMessageHandler);
		}

		// close all tabs
		tabManager.closeAll();
	},
	created() {
		this.$emit("check-dirty", this.checkDirty);
		this.$emit("handle-logout-save", this.handleLogoutSave);
		// App.vue disables the app-bar save/validate/discard/reset buttons
		// while this returns false (same emit-callback pattern as check-dirty)
		this.$emit("load-state", () => this.loadComplete);
	},

	methods: {
		...mapActions(useMainStore, [
			"setHelp",
			"clearDirty",
			"clearNotification",
			"clearPaper",
			"clearSaveUUID",
			"receiveSave",
			"receiveValidate",
			"setDirty",
			"setPaper",
			"setIsReworkSde",
			"setSaveInProgress",
			"setSaveUUID",
			"showNotification",
		]),
		async iframeInit() {
			this.moduleWindow = window.frames[this.module].contentWindow;

			// forward keyboard events to the iframe
			document.removeEventListener("keydown", this.keyDownHandler);
			document.addEventListener("keydown", this.keyDownHandler);

			// forward custom events to the iframe
			document.removeEventListener("next-cell", this.nextCellHandler);
			document.addEventListener("next-cell", this.nextCellHandler);

			this.$refs.iframe.contentWindow.removeEventListener(
				"resize",
				this.resizeHandler,
			);
			this.$refs.iframe.contentWindow.addEventListener(
				"resize",
				this.resizeHandler,
			);

			// set images for wpd
			if (this.module === "wpd") {
				const domain =
					import.meta.env.VUE_APP_ENV === "development"
						? `${this.domain}:8080`
						: this.domain;
				const url = `${this.protocol}://${domain}${tableAxisInstruction}`;

				interfaces[this.module].setOptions({
					tableAxisInstructionURL: url,
				});
			}

			interfaces[this.module].init(this.moduleWindow, this);

			await this.loadFiles();
		},
		clear() {
			this.autoSaveComplete = true;
			this.bounds = null;
			this.existingData = markRaw({});
			this.fileMap = {};
			this.isMounted = false;
			this.isUnmounted = true;
			this.loading = true;
			this.paperData = null;
			this.moduleWindow = null;
			this.preReworkDialog = false;
			this.skipDirtyCheck = false;
			this.tags = [];
			this.tagGroups = [];
			this.timer = null;
			this.userManualActive = false;
			this.validationData = null;
			this.validationDialog = false;
			this.saveRejectedByValidation = false;
			window.__outerPdfjsV4?.destroyAll?.();
			delete window.__outerPdfjsV4;
		},
		nextCellHandler() {
			this.moduleWindow.document?.dispatchEvent(new Event("next-cell"));
		},
		keyDownHandler(event) {
			const newEvent = new KeyboardEvent("keydown", {
				code: event.code,
				key: event.key,
				keyCode: event.keyCode,
				ctrlKey: event.ctrlKey,
				metaKey: event.metaKey,
				shiftKey: event.shiftKey,
			});

			this.moduleWindow.document?.dispatchEvent(newEvent);
		},
		resizeHandler: _.debounce(
			function () {
				// a trailing debounce can fire after unmount/dispose; skip
				// instead of throwing on the unbound singleton
				if (this.isUnmounted || !interfaces[this.module].isBound()) {
					return;
				}
				this.bounds = interfaces[this.module].getBounds();
			},
			300,
			{ trailing: true },
		),
		computeHash(str) {
			if (typeof str !== "string") return null;
			let h = 0;
			for (let i = 0; i < str.length; i++) {
				h = (h * 31 + str.charCodeAt(i)) | 0;
			}
			return h >>> 0;
		},
		checkWpdChanges: _.debounce(function () {
			const isDialogOpen = this.$refs.metadataV2?.isDialogOpen;

			if (
				this.readOnly ||
				this.isUnmounted ||
				!this.loadComplete ||
				isDialogOpen ||
				this.savingStopped
			) {
				return;
			}
			if (!this.isAuthenticated || _.isEmpty(this.paper)) {
				this.stopSaving();
				return;
			}

			const json = interfaces[this.module].saveJSON();
			const next = this.computeHash(json);
			if (next !== this.lastWpdHash) {
				const prevJson = this.lastWpdJson ?? json;
				const prevObj = JSON.parse(prevJson);
				const currObj = JSON.parse(json);
				const delta = jsonpatch.compare(prevObj, currObj);
				const isMeaningfulChange = this.isMeaningfulWpdChange(delta, prevObj);
				if (!isMeaningfulChange) {
					return;
				}

				// TODO: use the summary in the UI somehow
				// const summary = this.summarizeWpdChange(delta);

				this.lastWpdHash = next;
				this.setDirty();
				if (!this.isProgrammaticChange) {
					this.pushUndoSnapshot(json);
				}
				this.isProgrammaticChange = false;
				this.lastWpdJson = json;
			}
		}, 300),
		isMeaningfulWpdChange(delta, json) {
			const studyBranches = this.getStudyBranches(json);
			return delta.some((d) => {
				// adding a new TGI for an existing study branch is not a meaningful change
				if (d.op === "add" && typeof d.value === "object") {
					const md = d.value?.metadata ?? {};
					const hasNonBranchData = Object.keys(md)
						.filter((key) => !isNaN(key))
						.some(
							(key) => !this.compositeKeyUtils.isBranchTag(md[key].tag.name),
						);

					const hasNewBranch = !studyBranches.has(md.compositeKey);
					if (!hasNewBranch && !hasNonBranchData) {
						return false;
					}
				}

				// changing the file, page, rotation, and zoom are not meaningful changes
				if (
					d.path?.startsWith("/misc/current/page") ||
					d.path?.startsWith("/misc/current/zoom") ||
					d.path?.startsWith("/misc/current/file") ||
					d.path?.startsWith("/misc/rotation")
				) {
					return false;
				}

				return true;
			});
		},
		getStudyBranches(wpdJson) {
			const studyBranches = new Set();
			wpdJson.datasetColl.forEach((dataset) => {
				dataset.data.forEach((data) => {
					studyBranches.add(data.metadata.compositeKey);
				});
			});
			return studyBranches;
		},
		summarizeWpdChange(delta) {
			return delta.slice(0, 50).map(({ op, path, value, from }) => {
				const fmt = (v) =>
					typeof v === "string"
						? v.length > 80
							? v.slice(0, 77) + "..."
							: v
						: JSON.stringify(v);
				return {
					op,
					path,
					...(from ? { from } : {}),
					...(op !== "remove" ? { value: fmt(value) } : {}),
				};
			});
		},
		wpdActivityMessageHandler(event) {
			// Only consider simple forwarded UI events from WPD
			if (typeof event.data !== "string") return;
			if (["keydown", "resize"].includes(event.data)) {
				this.checkWpdChanges();
			}
		},
		isPreRework(createdDate) {
			const cutoff = new Date("2024-10-12T00:00:00Z");
			return !createdDate || createdDate.getTime() < cutoff.getTime();
		},
		getPaper() {
			return this.$http
				.get(`/papers/${this.paperUuid}`, {
					params: {
						saveUUID: this.sdeUuid,
						...(this.training ? { isTraining: true } : {}),
						dataFormat: "WPD",
					},
				})
				.then(async (response) => {
					this.paperData = response.data;

					// Store paper in Vuex for direct link support
					if (this.paperData.paper) {
						this.setPaper(this.paperData.paper);
					}

					// Set tags and tagGroups from response if available
					if (this.paperData.tags) {
						this.tags = this.paperData.tags;
					}
					if (this.paperData.tagGroups) {
						this.tagGroups = this.paperData.tagGroups;
					}

					// loose != also covers undefined: the API omits saveData
					// entirely for a brand-new extraction (JSON.stringify drops
					// undefined keys). The old strict !== null check fell into
					// this block and threw on saveData.data - silently swallowed
					// by the old catch, but a terminal load failure now.
					if (this.paperData.saveData != null) {
						const saveData = this.paperData.saveData;
						const saveJson = saveData.data;
						if (saveJson) {
							saveData.data = JSON.parse(saveJson);
						}

						const isPreRework = this.isPreRework(new Date(saveData.created_at));

						if (isPreRework) {
							this.preReworkDialog = true;
							this.setSaveUUID(uuidv4());
						} else {
							// Baseline for the WPD save guard: hash the exact raw
							// JSON string this client will diff against (before the
							// JSON.parse above mutated saveData.data). The pre-rework
							// branch intentionally keeps baselineHash null - it
							// abandons the stored doc and legitimately diffs
							// against an empty baseline.
							if (this.baselineSupported && saveJson) {
								this.baselineHash = await this.sha256Hex(saveJson);
							}
							this.existingData = markRaw(saveData);
							// Track rework state for App.vue button display
							this.setIsReworkSde(!!saveData.reopened_at);
						}

						// DCTL-XXXX (DHELP-437): retired by "Restart extraction".
						// The server refuses every save, so the editor hands over
						// to the read-only viewer; the viewer says why.
						if (saveData.superseded_at) {
							if (this.readOnly) {
								this.showNotification({
									message:
										"Superseded: this extraction was retired when its paper was restarted from scratch.",
									status: "info",
									timeout: 8000,
								});
							} else {
								this.openReadOnlyAfterSuperseded();
							}
						}
					}
				})
				.catch((error) => {
					// Terminal load failure: mounted() checks this flag and stops
					// before iframeInit/activateAutoSave, so a failed load can
					// never continue with empty tags and an empty document.
					this.loadFailed = true;

					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					} else {
						console.error("Failed to load the extraction paper:", error);
						this.showNotification({
							message: "Failed to load the extraction paper",
							status: "error",
						});
					}
				});
		},
		exposeOuterPdfjs(files) {
			// Lazy v4 PDFDocumentProxy cache keyed by file index (matches WPD's
			// FileManager.currentFileIndex). getDocument runs only on first access
			// for a given file, so supplementary PDFs the user never text-selects
			// in cost nothing. Cache is LRU-bounded — switching between many files
			// destroys() older docs to cap memory.
			const docCache = new Map();
			const lruOrder = [];
			const MAX_CACHED = 3;

			const loadDoc = async (fileIndex) => {
				const file = files[fileIndex];
				if (!file || file.type !== "application/pdf") return null;
				try {
					const buf = await file.arrayBuffer();
					return await pdfjsV4.getDocument({ data: buf }).promise;
				} catch (err) {
					console.warn("outer pdfjs v4 load failed for", file.name, err);
					return null;
				}
			};

			const touchLru = (fileIndex) => {
				const at = lruOrder.indexOf(fileIndex);
				if (at >= 0) lruOrder.splice(at, 1);
				lruOrder.push(fileIndex);
				while (lruOrder.length > MAX_CACHED) {
					const evictIdx = lruOrder.shift();
					const evicted = docCache.get(evictIdx);
					docCache.delete(evictIdx);
					evicted?.then((doc) => doc?.destroy?.()).catch(() => {});
				}
			};

			const destroyAll = () => {
				for (const p of docCache.values()) {
					p?.then((doc) => doc?.destroy?.()).catch(() => {});
				}
				docCache.clear();
				lruOrder.length = 0;
			};

			window.__outerPdfjsV4 = {
				pdfjsLib: pdfjsV4,
				getDoc: (fileIndex) => {
					if (fileIndex == null || fileIndex < 0 || fileIndex >= files.length) {
						return null;
					}
					let p = docCache.get(fileIndex);
					if (!p) {
						p = loadDoc(fileIndex);
						docCache.set(fileIndex, p);
					}
					touchLru(fileIndex);
					return p;
				},
				destroyAll,
			};
		},

		async loadFiles() {
			this.fileMap = {};

			// convert base64 data to File objects
			this.paperData.fileData.forEach((data) => {
				const file = new File(
					[utils.base64ToBlob(data.data, data.type)],
					data.name,
					{
						type: data.type,
					},
				);

				this.fileMap[data.uuid] = file;
			});

			this.exposeOuterPdfjs(Object.values(this.fileMap));

			// load files into module
			return interfaces[this.module]
				.loadFiles(Object.values(this.fileMap))
				.then(async () => {
					// get image bounds
					this.bounds = interfaces[this.module].getBounds();

					// script injection for additional UIs
					interfaces[this.module].inject(this);

					// load json file if it exists
					if (this.existingData) {
						await interfaces[this.module].loadJSON(
							this,
							_.cloneDeep(this.existingData.data),
							this.dataPointUuid ?? null,
						);
					} else {
						this.loading = false;
					}
				});
		},
		discardSDE() {
			// Load gate: discarding is destructive (deletes the stored doc);
			// never act on a page that has not finished loading its own data.
			if (!this.loadComplete) {
				this.showNotification({
					message: "This extraction is still loading. Nothing was discarded.",
					status: "warning",
				});
				this.receiveSave();
				return;
			}

			this.$http
				.post(`/data-entry/${this.sdeUuid}/delete`)
				.then(() => {
					this.showNotification({
						message: "Data discarded successfully",
						status: "success",
					});

					this.existingData = markRaw({});
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					// consume save event
					this.receiveSave();

					// resolve if caller is a promise
					if (this.resolve) {
						this.resolve();
					}

					this.skipDirtyCheck = true;

					this.$router.push("/home");
				});
		},

		resetJSON() {
			// Load gate: reset deletes the stored S3 doc; same rule as discard.
			if (!this.loadComplete) {
				this.showNotification({
					message: "This extraction is still loading. Nothing was reset.",
					status: "warning",
				});
				this.receiveSave();
				return;
			}

			this.$http
				.post(`/data-entry/${this.sdeUuid}`, {
					data: null,
					done: this.done,
					paperUUID: this.paperUuid,
					dataFormat: "WPD",
					isReset: true,
					...(this.baselineSupported
						? {
								baselineHash: this.baselineHash,
								baselineEmpty: this.baselineHash === null,
							}
						: {}),
					...(this.training ? { isTraining: true } : {}),
				})
				.then(() => {
					this.showNotification({
						message: "Data reset successfully",
						status: "success",
					});

					this.existingData = markRaw({});
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					// consume save event
					this.receiveSave();

					// resolve if caller is a promise
					if (this.resolve) {
						this.resolve();
					}

					window.location.reload();
				});
		},

		async saveJSON(json, isAutosave = false) {
			// Load gate (funnel): no save may run before this page's own
			// document has fully loaded, whatever the trigger. See
			// handleSaveBeforeLoad for the incident this prevents.
			if (!this.loadComplete) {
				this.handleSaveBeforeLoad();
				return;
			}

			// abort if there is already a save in-progress
			if (this.readOnly || (!isAutosave && this.saveInProgress)) {
				return;
			}

			// the blocking dialog is the only surface once saving has stopped;
			// consume the save event so a waiting caller is released
			if (this.savingStopped) {
				this.receiveSave();
				return;
			}
			if (!this.isAuthenticated || _.isEmpty(this.paper)) {
				this.stopSaving();
				this.receiveSave();
				return;
			}

			this.setSaveInProgress(true);
			const versionedJSON = this.addDactylVersion(json);

			const delta = jsonpatch.compare(
				this.existingData?.data ?? {},
				JSON.parse(versionedJSON),
			);

			const metadataRef = this.$refs.metadataV2;
			const isDialogOpen = metadataRef?.isDialogOpen;
			const isTextSelectActive = metadataRef?.textSelect?.ctx?.visible;
			if (isDialogOpen || isTextSelectActive) {
				this.setSaveInProgress(false);
				return;
			}

			// save json to server
			return this.$http
				.post(`/data-entry/${this.sdeUuid}`, {
					delta,
					done: this.done,
					paperUUID: this.paperUuid,
					dataFormat: "WPD",
					// prove to the server that this client's diff baseline is
					// the currently stored doc (409 baseline_conflict otherwise)
					...(this.baselineSupported
						? {
								baselineHash: this.baselineHash,
								baselineEmpty: this.baselineHash === null,
							}
						: {}),
					...(this.training ? { isTraining: true } : {}),
				})
				.then(async (response) => {
					// The document is stored, so any earlier refusal no longer
					// describes the state of this extraction (DCTL-2459).
					this.saveRejectedByValidation = false;
					this.lastSuccessfulSaveAt = new Date();
					if (this.saveConnectionLost) {
						this.saveConnectionLost = false;
						this.clearNotification();
					}

					// Adopt the new baseline: prefer the server's hash of the
					// exact stored string; fall back to hashing the JSON just
					// sent (mirrors how existingData.data is updated below).
					if (this.baselineSupported) {
						this.baselineHash =
							response.data.baselineHash ??
							(await this.sha256Hex(versionedJSON));
					}

					if (this.done) {
						this.showNotification({
							message: "Paper saved and marked as done",
							status: "success",
						});

						// clear paper from store
						this.clearPaper();

						// clear save UUID
						this.clearSaveUUID();
					} else {
						if (this.existingData) {
							this.existingData.data = markRaw(JSON.parse(versionedJSON));
						} else {
							this.existingData = markRaw({
								data: JSON.parse(versionedJSON),
								created_at: new Date().toLocaleString(),
							});
						}
						// display success
						this.showNotification({
							message: response.data.message,
							status: response.data.status,
							timeout: 1000,
						});

						// send message to raw data viewer tab to refresh
						const rawDataTab = tabManager.getTab("raw-data-viewer");
						if (rawDataTab && !rawDataTab.closed) {
							rawDataTab.postMessage(
								{
									type: "SAVE_COMPLETED",
								},
								window.location.origin,
							);
						}
					}

					const navigate = !this.validationDialog && (this.done || this.exit);
					if (navigate) {
						// skip navigation confirmation
						this.skipDirtyCheck = true;

						// redirect to dashboard
						this.$router.push("/home");
					}
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data?.code === "validation_failed") {
							this.validationData = error.response.data;
							this.validationTriggeredBySave = true;
							this.validationDialog = true;
							this.saveRejectedByValidation = true;
						} else if (
							error.response.data?.code === "paper_no_longer_available"
						) {
							// Save-time race: another extractor claimed the paper
							// past the 2-extractor cap between this user's
							// "Start Extraction" click and this first save. Show
							// a dialog and bounce them back to the queue so the
							// in-progress (now orphaned) tab doesn't keep posting.
							this.unavailableDialogOpen = true;
						} else if (error.response.data?.code === "extraction_superseded") {
							// DCTL-XXXX (DHELP-437): a tab opened before the paper
							// was restarted. Retrying can only 409 again.
							this.openReadOnlyAfterSuperseded();
						} else if (error.response.data?.code === "baseline_conflict") {
							// The stored doc no longer matches what this session
							// loaded (another session saved, or this session's
							// state is stale). Do NOT auto-retry: re-diffing
							// against a stale baseline is exactly the corruption
							// vector. The user must reload to resync.
							this.showNotification({
								message:
									error.response.data.message ||
									"Save rejected: this extraction changed since it was loaded. Reload the page to continue.",
								status: "error",
								persist: true,
							});
						} else if (error.response.data.message) {
							this.showNotification(error.response.data);
							if (error.response.data.code === "invalid_data") {
								throw new Error(error.response.data.message);
							}
						} else if (error.response.data.error) {
							this.showNotification(error.response.data.error);
						} else {
							this.showNotification("SDE save failed");
						}
					} else {
						// no response at all: the document is still in this tab
						// and the autosave / dialog-close saves retry it in full,
						// so persist the warning until one gets through
						this.saveConnectionLost = true;
						this.showNotification({
							message: SAVE_CONNECTION_LOST_MESSAGE,
							status: "warning",
							persist: true,
						});
					}
				})
				.finally(() => {
					this.setSaveInProgress(false);

					// consume save event
					this.receiveSave();

					// resolve if caller is a promise
					if (this.resolve) {
						this.resolve();
					}
				});
		},
		validateJSON() {
			// Load gate (funnel): see handleSaveBeforeLoad
			if (!this.loadComplete) {
				this.showNotification({
					message:
						"This extraction is still loading. Validation is not available yet.",
					status: "warning",
				});
				this.receiveValidate();
				return;
			}

			this.$http
				.post(`/data-entry/${this.sdeUuid}/validate`)
				.then((response) => {
					this.validationData = response.data;
					this.validationTriggeredBySave = false;
					this.validationDialog = true;
				})
				.catch((error) => {
					this.showNotification({
						message: `Error while attempting to validate: ${error.response?.data?.message}`,
						status: "error",
					});
				})
				.finally(() => {
					this.receiveValidate();
				});
		},
		addDactylVersion(json) {
			const object = JSON.parse(json);

			object.dactylVersion = this.$version;
			if (!object.tags) {
				object.tags = this.tags;
			}

			if (!object.tagGroups) {
				object.tagGroups = this.tagGroups;
			}

			return JSON.stringify(object);
		},
		getTags() {
			return this.tags;
		},

		// Union of tags belonging to tag groups marked supports_graph_table,
		// deduped by name. Used to drive the graph calibration axis Data
		// Type dropdown so it only suggests tags actually configured for
		// graph/table extraction (matches SDEv2's `allGraphTags`).
		getGraphEligibleTags() {
			const seen = new Map();
			for (const tg of this.tagGroups || []) {
				if (!tg.supports_graph_table) continue;
				for (const tag of tg.tags || []) {
					if (tag.isCompositeKeyMember) continue;
					if (!seen.has(tag.name)) seen.set(tag.name, tag);
				}
			}
			return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
		},

		getTagGroups() {
			return this.tagGroups;
		},
		captureMetadata(args) {
			return this.$refs.metadataV2.open(args);
		},
		closeMetadataDialog() {
			return this.$refs.metadataV2.close();
		},
		openMetadataPreview(args) {
			return this.$refs.metadataPreview.open(args, this.tags);
		},
		closeMetadataPreview() {
			return this.$refs.metadataPreview.close();
		},
		openMetadataSelectDialog(args) {
			return this.$refs.metadataSelectDialog.open(args, this.tags);
		},
		openSwitchTagGroupDialog(args) {
			return this.$refs.switchTagGroupDialog.open(args);
		},
		showNoHeaderHint(args) {
			const article = {
				row: "row does",
				col: "column does",
				both: "row and column do",
			};

			const messages = [
				`This ${article[args.mode]} not have header information.`,
				"Add data to the headers by selecting the axis, and then you can add data here.",
			];

			this.$refs.hint.open({
				color: "error",
				messages: messages,
				position: args.position,
			});
		},
		hideNoHeaderHint() {
			this.$refs.hint.close();
		},
		checkDirty() {
			// Pre-load (or post-unmount: App.vue keeps this callback after the
			// page is gone) nothing of this page's can be dirty, and the WPD
			// singleton may be unbound or hold another extraction's document.
			// Treat as not-dirty; this is what convinced autoLogout there was
			// something to save during the incident.
			if (!this.loadComplete) {
				return false;
			}

			const currentJson = interfaces[this.module].saveJSON();
			const currentVersionedJson = this.addDactylVersion(currentJson);
			const currentVersionedData = JSON.parse(currentVersionedJson);

			// prevent setting dirty flag if axesColl and datasetColl is empty
			// the cause of this intermittent bug has not been identified
			const noData =
				currentVersionedData.axesColl.length === 0 &&
				currentVersionedData.datasetColl.length === 0;

			const existingData = this.existingData?.data;
			if (
				(noData && !existingData) ||
				_.isEqual(
					_.omit(currentVersionedData, "dactylVersion"),
					_.omit(existingData, "dactylVersion"),
				)
			) {
				this.clearDirty();
				return false;
			}

			// the store dirty flag is for warning user that unsaved changes
			// will be lost
			this.setDirty();

			return true;
		},
		async timedAutoSave() {
			const metadataRef = this.$refs.metadataV2;
			const isDialogOpen = metadataRef?.isDialogOpen;
			const isTextSelectActive = metadataRef?.textSelect?.ctx?.visible;

			// Do not autosave if the metadata dialog or text select is open
			if (
				this.readOnly ||
				this.savingStopped ||
				!this.$refs ||
				!metadataRef ||
				isDialogOpen ||
				isTextSelectActive
			) {
				this.setSaveInProgress(false);
				return;
			}

			if (this.checkDirty()) {
				// save json
				await this.saveJSON(interfaces[this.module].saveJSON(), true);
			} else {
				this.setSaveInProgress(false);
			}
		},
		async autoSaveMetadata() {
			if (this.readOnly || this.saveInProgress) {
				return;
			}

			// handleLogoutSave waits on autoSaveComplete; release it even though
			// nothing is saved once saving has stopped
			if (this.savingStopped) {
				this.autoSaveComplete = true;
				return;
			}

			if (this.checkDirty()) {
				const saveData = interfaces[this.module].saveJSON(this);
				await this.saveJSON(saveData);
			}

			this.autoSaveComplete = true;
		},
		activateAutoSave() {
			// create the worker if necessary
			if (!this.timer) {
				this.timer = new Timer();
			}

			// listen for timer messages
			this.timer.onmessage = async ({ data }) => {
				// process interval ticks
				if (data !== "interval_tick" || this.saveInProgress) {
					return;
				}
				if (this.savingStopped) {
					return;
				}
				if (!this.isAuthenticated) {
					this.stopSaving();
					return;
				}
				this.setSaveInProgress(true);
				await this.timedAutoSave();
			};

			// start the interval
			this.timer.postMessage({
				action: "start_interval",
				duration: autoSaveWait,
			});
		},
		deactivateAutoSave() {
			this.timer?.postMessage({ action: "stop_interval" });
		},
		hintKeydownHandler(event) {
			switch (event.key) {
				case "Escape":
					this.hideNoHeaderHint();
					break;
			}
		},
		waitForIframeLoad() {
			return new Promise((resolve) => {
				this.$refs.iframe.removeEventListener("load", resolve);
				this.$refs.iframe.addEventListener("load", resolve);
			});
		},
		openFileInNewTab() {
			// pre-load there is no current file/page to open yet
			if (!this.loadComplete) {
				this.showNotification({
					message: "This extraction is still loading.",
					status: "warning",
				});
				return;
			}

			// get current file index and page number
			const { file: fileIndex, page: pageNumber } =
				interfaces[this.module].getCurrentLocationInfo();

			// create tab key that includes the file index to allow multiple file-viewer tabs
			const tabKey = `file-viewer-${fileIndex}`;

			// helper function to create HTML content and blob URLs
			const createHtmlContent = () => {
				// get file at current file index
				const fileData = Object.values(this.fileMap)[fileIndex];

				// create blob URL for the PDF
				const objectUrl = URL.createObjectURL(fileData);

				// create HTML content using the template
				const htmlContent = createPdfViewerHtml(objectUrl, pageNumber);

				// create blob URL for the HTML content
				const htmlBlob = new Blob([htmlContent], { type: "text/html" });
				const htmlUrl = URL.createObjectURL(htmlBlob);

				return { objectUrl, htmlUrl };
			};

			// helper function to set up cleanup for blob URLs
			const setupCleanup = (tab, objectUrl) => {
				// remove any existing load listeners to prevent duplicates
				const existingLoadHandler = tab._pdfLoadHandler;
				if (existingLoadHandler) {
					tab.removeEventListener("load", existingLoadHandler);
				}

				// create a new load handler
				const loadHandler = () => {
					try {
						const pdfEmbed = tab.document.querySelector(
							`embed[type="application/pdf"]`,
						);
						if (pdfEmbed) {
							// remove any existing PDF load listeners to prevent duplicates
							const existingPdfLoadHandler = pdfEmbed._pdfLoadHandler;
							if (existingPdfLoadHandler) {
								pdfEmbed.removeEventListener("load", existingPdfLoadHandler);
							}

							// create a new PDF load handler
							const pdfLoadHandler = () => {
								URL.revokeObjectURL(objectUrl);
								// clean up the handler reference
								pdfEmbed._pdfLoadHandler = null;
							};

							// store the handler reference and add the listener
							pdfEmbed._pdfLoadHandler = pdfLoadHandler;
							pdfEmbed.addEventListener("load", pdfLoadHandler);
						}
					} catch (e) {
						// cross-origin restrictions might prevent access
						// fall back to the timeout approach
						setTimeout(() => {
							URL.revokeObjectURL(objectUrl);
						}, 500);
					}
					// clean up the handler reference
					tab._pdfLoadHandler = null;
				};

				// store the handler reference and add the listener
				tab._pdfLoadHandler = loadHandler;
				tab.addEventListener("load", loadHandler);
			};

			// check if a file viewer tab already exists for this specific file
			if (tabManager.hasTab(tabKey) && tabManager.isTabOpen(tabKey)) {
				// focus the existing tab and navigate to the current page
				const existingTab = tabManager.getTab(tabKey);
				existingTab.focus();

				// navigate to the current page by creating new HTML content and navigating to it
				try {
					const { objectUrl, htmlUrl } = createHtmlContent();

					// navigate the existing tab to the new HTML content
					existingTab.location.href = htmlUrl;

					// set up cleanup for the PDF blob URL
					setupCleanup(existingTab, objectUrl);
				} catch (e) {
					// cross-origin restrictions might prevent access
					console.warn(
						"Could not update page in existing tab due to cross-origin restrictions",
					);
				}
				return;
			}

			// create new HTML content and blob URLs
			const { objectUrl, htmlUrl } = createHtmlContent();

			// open the HTML page in a new tab with the file-specific key
			const newTab = tabManager.openTab(htmlUrl, tabKey);

			// set up cleanup for the PDF blob URL
			if (newTab) {
				setupCleanup(newTab, objectUrl);
			}
		},
		// Save-time race recovery: dismiss the dialog and route the user
		// back to the work queue. The user's in-progress local work is
		// dropped on purpose since the paper isn't theirs to extract
		// anymore. skipDirtyCheck bypasses the standard unsaved-changes
		// confirm in beforeRouteLeave; the work can't be saved by
		// definition, so prompting "do you really want to leave?" would
		// be misleading.
		returnToQueueAfterUnavailable() {
			this.unavailableDialogOpen = false;
			this.skipDirtyCheck = true;
			this.$router.push("/home");
		},
		// Funnel gate for save triggers that fire before this extraction has
		// finished loading (store save watcher, logout-save, autosave). At
		// that point the WPD singleton is unbound or still holds a PREVIOUS
		// extraction's document, and existingData is empty - so a save would
		// diff a stale/empty doc against {} and wholesale-replace this
		// extraction's stored data (the Jul 2026 cross-SDE corruption).
		// Visible no-op: notify, consume the pending save event, and honor an
		// exit request by navigating away without saving (there is nothing of
		// this page's to save).
		handleSaveBeforeLoad() {
			// receiveSave() clears the exit flag; capture it first
			const exitRequested = this.exit;

			// consume the save event (also resolves any pending save promise)
			this.receiveSave();

			const message = this.loadFailed
				? "This extraction failed to load. Nothing was saved."
				: "This extraction is still loading. Nothing was saved.";

			if (exitRequested) {
				this.skipDirtyCheck = true;
				// notify only after the navigation settles: beforeUnmount
				// clears any active notification during page teardown, which
				// would silently swallow a toast shown before the push
				this.$router.push("/home").finally(() => {
					this.showNotification({ message, status: "warning" });
				});
			} else {
				this.showNotification({ message, status: "warning" });
			}
		},
		// SHA-256 hex digest of a string (UTF-8), used for the WPD save
		// baseline guard. Callers must check baselineSupported first.
		async sha256Hex(str) {
			const bytes = new TextEncoder().encode(str);
			const digest = await window.crypto.subtle.digest("SHA-256", bytes);
			return Array.from(new Uint8Array(digest))
				.map((byte) => byte.toString(16).padStart(2, "0"))
				.join("");
		},
		// Terminal load-failure affordances: a full reload re-runs the whole
		// mount flow (iframe, singleton init, paper fetch) from scratch.
		retryLoad() {
			window.location.reload();
		},
		returnHomeAfterLoadFailure() {
			this.skipDirtyCheck = true;
			this.$router.push("/home");
		},
		// DCTL-XXXX (DHELP-437): leave the editor for the read-only viewer of
		// the same extraction. replace(), so Back does not return to an editor
		// whose every save would be refused.
		openReadOnlyAfterSuperseded() {
			this.showNotification({
				message:
					"This extraction was retired when its paper was restarted from scratch. It can still be viewed, but it can no longer be changed.",
				status: "error",
				persist: true,
			});
			this.skipDirtyCheck = true;
			const { paperUuid, sdeUuid } = this.$route.params;
			this.$router.replace(`/data-entry-read-only/${paperUuid}/${sdeUuid}`);
		},
		// DCTL-2654: the session or the store paper is gone while this page
		// still renders a loaded document. Stop every save path and show the
		// blocking dialog; nothing else on the page reports the loss.
		stopSaving() {
			// nothing of this page's is being saved in read-only mode
			if (this.savingStopped || this.readOnly) {
				return;
			}
			const block = shouldBlockSaving({
				loadComplete: this.loadComplete,
				isMounted: this.isMounted,
				isAuthenticated: this.isAuthenticated,
				paperEmpty: _.isEmpty(this.paper),
			});
			if (!block) {
				return;
			}
			this.savingStopped = true;
			this.deactivateAutoSave();
		},
		// Both exits are full navigations: the store is already reset, so a
		// router push would be re-guarded against a session that is gone.
		reloadAfterSavingStopped() {
			this.skipDirtyCheck = true;
			window.location.reload();
		},
		returnHomeAfterSavingStopped() {
			this.skipDirtyCheck = true;
			window.location.assign("/home");
		},
		handleQcCommentNavigate(dataPointUuid, candidates) {
			if (!dataPointUuid || !interfaces[this.module]?.navigateToDataPoint) {
				return;
			}
			// Close metadata dialog before navigating to avoid
			// creating duplicate data points
			this.$refs.metadataV2?.close();
			// DCTL-2746: a comment written before each input's points were
			// stored separately lists both inputs' points, Input A's first.
			// Try them in order until one is in this extraction.
			const uuids = candidates?.length ? candidates : [dataPointUuid];
			this.$nextTick(async () => {
				for (const uuid of uuids) {
					if (await interfaces[this.module].navigateToDataPoint(uuid)) return;
				}
			});
		},
		openRawDataViewer() {
			// pre-load the singleton has no document of this page's to show
			if (!this.loadComplete) {
				this.showNotification({
					message: "This extraction is still loading.",
					status: "warning",
				});
				return;
			}

			this.rawData = JSON.parse(interfaces[this.module].saveJSON());
			// check if a raw data viewer tab already exists
			if (
				tabManager.hasTab("raw-data-viewer") &&
				tabManager.isTabOpen("raw-data-viewer")
			) {
				// focus the existing tab
				const existingTab = tabManager.getTab("raw-data-viewer");
				existingTab.focus();
				return;
			}

			// open the raw data viewer in a new tab
			const url = `/raw-data-viewer?uuid=${encodeURIComponent(this.sdeUuid)}`;
			this.rawDataTab = tabManager.openTab(url, "raw-data-viewer");
		},
		async handleLogoutSave() {
			const metadataRef = this.$refs.metadataV2;
			const isDialogOpen = metadataRef?.isDialogOpen;

			if (isDialogOpen) {
				// close the metadata dialog and export the existing metadata
				this.autoSaveComplete = false;

				metadataRef.resolve(metadataRef.exportMetadata());

				// this is some hackery - it's tough to synchronously wait for the post-processing and saving
				// that happens when we closed the metadata dialog.  Instead, simply wait for "autoSaveComplete"
				// variable to be set to true, which will happen after post-processing and after the save completes.
				await new Promise((resolve) => {
					const unwatch = this.$watch(
						"autoSaveComplete",
						(newValue) => {
							if (newValue) {
								unwatch();
								resolve();
							}
						},
						{ immediate: true },
					);
				});
			} else {
				await this.autoSaveMetadata();
			}
		},
		async updateJson(json) {
			this.loading = true;
			await interfaces[this.module].loadJSON(this, JSON.parse(json));
		},

		async navigateToDataPoint(dataPointUuid) {
			if (!dataPointUuid) {
				console.warn("No data point UUID provided");
				return;
			}

			try {
				this.isProgrammaticChange = true;
				this.loading = true;

				// navigate to the data point (switches file/page, sets zoom/scroll, selects/highlights)
				const locationInfo =
					await interfaces[this.module].navigateToDataPoint(dataPointUuid);

				if (!locationInfo) {
					console.warn(`Could not navigate to data point ${dataPointUuid}`);
					this.showNotification({
						message: "Data point not found",
						status: "warning",
					});
					return;
				}

				console.log("Successfully navigated to data point:", dataPointUuid);
			} catch (error) {
				console.error("Error navigating to data point:", error);
				this.showNotification({
					message: "Failed to navigate to data point",
					status: "error",
				});
			} finally {
				this.loading = false;
				this.isProgrammaticChange = false;
			}
		},

		handleMessage(event) {
			// handle NAVIGATE_TO_DATA_POINT message (from parent iframe)
			if (event.data.type === "NAVIGATE_TO_DATA_POINT") {
				const isDev = import.meta.env.VUE_APP_ENV === "development";
				const isLocalhost =
					event.origin.includes("localhost") ||
					event.origin.includes("127.0.0.1");

				if (!isDev && event.origin !== window.location.origin) {
					return;
				}

				if (isDev && !isLocalhost) {
					console.warn(
						"[DataEntry] Rejected NAVIGATE_TO_DATA_POINT from non-localhost origin:",
						event.origin,
					);
					return;
				}

				console.log(
					"[DataEntry] Processing NAVIGATE_TO_DATA_POINT message",
					event.data,
				);
				const { dataPointUuid } = event.data;
				if (dataPointUuid) {
					this.navigateToDataPoint(dataPointUuid);
				}
				return;
			}

			// For all other message types, require exact origin match
			if (event.origin !== window.location.origin) {
				console.warn(
					"[DataEntry] Rejected message from different origin:",
					event.origin,
				);
				return;
			}

			// handle UPDATE_JSON_FROM_VIEWER message (from raw data viewer)
			if (event.data.type === "UPDATE_JSON_FROM_VIEWER") {
				this.updateJson(event.data.json);
				return;
			}
		},
		article(word) {
			return indefinite(word, { capitalize: true });
		},
		pushUndoSnapshot(json) {
			// Avoid duplicates
			if (this.lastSnapshotJson === json) return;
			this.undoStack.push({
				json: json,
				path: interfaces[this.module].getSelectedPaths(),
			});
			interfaces[this.module].enableUndo();
			this.lastSnapshotJson = json;
			if (this.undoStack.length > this.maxHistory) {
				this.undoStack.splice(0, this.undoStack.length - this.maxHistory);
			}

			this.redoStack = [];
			interfaces[this.module].disableRedo();
		},
		async undo() {
			if (this.undoStack.length <= 1) return;
			const current = this.undoStack.pop();

			if (this.undoStack.length <= 1) {
				interfaces[this.module].disableUndo();
			}
			this.redoStack.push(current);
			interfaces[this.module].enableRedo();
			const prev = this.undoStack[this.undoStack.length - 1];
			await this.applySnapshot(prev.json);
			interfaces[this.module].selectPath(current.path.root, current.path.axis);
		},
		async redo() {
			if (this.redoStack.length === 0) return;
			const next = this.redoStack.pop();
			if (this.redoStack.length === 0) {
				interfaces[this.module].disableRedo();
			}
			this.undoStack.push(next);
			interfaces[this.module].enableUndo();
			await this.applySnapshot(next.json);
			interfaces[this.module].selectPath(next.path.root, next.path.axis);
		},
		async applySnapshot(json) {
			try {
				this.isProgrammaticChange = true;
				this.loading = true;
				await interfaces[this.module].loadJSON(this, JSON.parse(json));
				this.lastSnapshotJson = json;
				this.lastWpdHash = this.computeHash(json);
			} finally {
				this.loading = false;
				this.isProgrammaticChange = false;
			}
		},
	},
};
</script>

<style scoped lang="scss">
html {
	overflow: hidden !important;
}

iframe {
	width: 100%;
	height: 100%;
	border: 0;
}

.v-btn--fab.v-size--small.v-btn--absolute.v-btn--bottom.menu-button {
	bottom: 25px;
	right: 25px;
	z-index: 204;
}

// Sits over the WPD iframe, just under the app bar that holds Save, so the
// refusal stays in view while the curator fixes the flagged record.
.save-rejected-banner {
	position: fixed;
	top: 72px;
	left: 50%;
	transform: translateX(-50%);
	width: auto;
	max-width: 720px;
	z-index: 205;
}
</style>
